/**
 * 日期时间格式化工具函数
 */

/**
 * 格式化时间戳为日期字符串
 * @param {string|number} timestamp - 时间戳（支持秒级和毫秒级）
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns {string} 格式化后的日期字符串，无效时返回原始值或 '-'
 */
export function formatTimestamp(timestamp, format = 'YYYY-MM-DD') {
  if (!timestamp) {
    return '-'
  }

  // 如果已经是字符串格式，直接返回
  if (typeof timestamp === 'string' && timestamp.includes('-')) {
    return timestamp
  }

  // 转换为数字
  let ts = Number(timestamp)
  if (isNaN(ts)) {
    return timestamp
  }

  // 如果是秒级时间戳（10位），转换为毫秒级（13位）
  if (ts < 10000000000) {
    ts = ts * 1000
  }

  const date = new Date(ts)
  if (isNaN(date.getTime())) {
    return timestamp
  }

  return formatDate(date, format)
}

/**
 * 格式化日期对象为字符串
 * @param {Date} date - 日期对象
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD'
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(date, format = 'YYYY-MM-DD') {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '-'
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 格式化日期时间为完整字符串（包含时分秒）
 * @param {string|number|Date} dateTime - 日期时间（时间戳或Date对象）
 * @param {string} format - 日期格式，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} 格式化后的日期时间字符串
 */
export function formatDateTime(dateTime, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!dateTime) {
    return '-'
  }

  let date
  if (dateTime instanceof Date) {
    date = dateTime
  } else if (typeof dateTime === 'string' && dateTime.includes('-')) {
    // 如果已经是字符串格式，尝试解析
    date = new Date(dateTime)
    if (isNaN(date.getTime())) {
      return dateTime
    }
  } else {
    // 时间戳
    let ts = Number(dateTime)
    if (isNaN(ts)) {
      return dateTime
    }
    // 如果是秒级时间戳（10位），转换为毫秒级（13位）
    if (ts < 10000000000) {
      ts = ts * 1000
    }
    date = new Date(ts)
    if (isNaN(date.getTime())) {
      return dateTime
    }
  }

  // 扩展毫秒占位符 SSS
  const base = formatDate(date, format)
  if (format.includes('SSS')) {
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0')
    return base.replace('SSS', milliseconds)
  }
  return base
}

/**
 * 当前本地日期时间字符串：日期段与时刻段用连字符衔接，时刻内为冒号
 * @returns {string} 如 "20260413145050"；无效日期时返回 "-"
 */
export function getCurrentDateTimeDashString() {
  return formatDate(new Date(), 'YYYYMMDDHHmmss')
}

/**
 * 格式化到期时间为「xxxx年x月x日」
 * @param {string|number|Date} time - 时间（时间戳、ISO 字符串或 Date 对象）
 * @returns {string} 如 "2025年1月24日"，无效时返回空字符串
 */
export function formatExpirationDate(time) {
  if (!time) return ''
  const date = new Date(time)
  if (isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${year}年${month}月${day}日`
}

/**
 * 格式化到期时间为续费截止提示文案：「xxxx年x月x日前续费无需重新提交申请审核」
 * @param {string|number|Date} expirationTime - 到期时间（时间戳、ISO 字符串或 Date 对象）
 * @returns {string} 如 "2025年1月24日前续费无需重新提交申请审核"，无效时返回空字符串
 */
export function formatRenewalDeadlineHint(expirationTime) {
  const date = new Date(expirationTime)
  if (isNaN(date.getTime())) return ''
  // 在到期时间基础上顺延 30 天
  const deadline = new Date(date.getTime() + 30 * 24 * 60 * 60 * 1000)
  const year = deadline.getFullYear()
  const month = deadline.getMonth() + 1
  const day = deadline.getDate()
  return `${year}年${month}月${day}日前续费无需重新提交申请审核`
}